/*!
 * TKO JSX Rendering 🥊  tko.utils.jsx@4.0.0-alpha5c
 * (c) The Knockout.js Team - https://tko.io/
 * License: MIT (http://www.opensource.org/licenses/mit-license.php)
 */

import { removeNode, addDisposeCallback } from 'tko.utils';
import { isObservable, unwrap } from 'tko.observable';
import { contextFor, applyBindings } from 'tko.bind';
import { NativeProvider } from 'tko.provider.native';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __values(o) {
    var m = typeof Symbol === "function" && o[Symbol.iterator], i = 0;
    if (m) return m.call(o);
    return {
        next: function () {
            if (o && i >= o.length) o = void 0;
            return { value: o && o[i++], done: !o };
        }
    };
}

function __read(o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
}

function __spread() {
    for (var ar = [], i = 0; i < arguments.length; i++)
        ar = ar.concat(__read(arguments[i]));
    return ar;
}

var ORIGINAL_JSX_SYM = Symbol('Knockout - Original JSX');
/**
 *
 * @param {any} possibleJsx Test whether this value is JSX.
 *
 * True for
 *    { elementName }
 *    [{elementName}]
 *    observable({elementName} | [])
 *
 * Any observable will return truthy if its value is an array that doesn't
 * contain HTML elements.  Template nodes should not be observable unless they
 * are JSX.
 *
 * There's a bit of guesswork here that we could nail down with more test cases.
 */
function maybeJsx(possibleJsx) {
    if (isObservable(possibleJsx)) {
        return true;
    }
    var value = unwrap(possibleJsx);
    if (!value) {
        return false;
    }
    if (value.elementName) {
        return true;
    }
    if (!Array.isArray(value) || !value.length) {
        return false;
    }
    if (value[0] instanceof window.Node) {
        return false;
    }
    return true;
}
/**
 * Clone a node from its original JSX if possible, otherwise using DOM cloneNode
 * This preserves any native attributes set by JSX.
 * @param {HTMLElemen} node
 * @return {HTMLElement} clone of node
 */
function cloneNodeFromOriginal(node) {
    if (!node) {
        return [];
    }
    if (node[ORIGINAL_JSX_SYM]) {
        var possibleTemplate = jsxToNode(node[ORIGINAL_JSX_SYM]);
        return __spread(possibleTemplate.content
            ? possibleTemplate.content.childNodes
            : possibleTemplate.childNodes);
    }
    if ('content' in node) {
        var clone = document.importNode(node.content, true);
        return __spread(clone.childNodes);
    }
    var nodeArray = Array.isArray(node) ? node : [node];
    return nodeArray.map(function (n) { return n.cloneNode(true); });
}
/**
 * Use a JSX transpilation of the format created by babel-plugin-transform-jsx
 * @param {Object} jsx An object of the form
 *    { elementName: node-name e.g. "div",
 *      attributes: { "attr": "value", ... },
 *      children: [string | jsx]
 *    }
 */
function jsxToNode(jsx) {
    if (typeof jsx === 'string') {
        return document.createTextNode(jsx);
    }
    var node = document.createElement(jsx.elementName);
    var subscriptions = [];
    /** Slots need to be able to replicate with the attributes, which
     *  are not preserved when cloning from template nodes. */
    node[ORIGINAL_JSX_SYM] = jsx;
    updateAttributes(node, unwrap(jsx.attributes), subscriptions);
    if (isObservable(jsx.attributes)) {
        subscriptions.push(jsx.attributes.subscribe(function (attrs) {
            updateAttributes(node, unwrap(attrs), subscriptions);
        }));
    }
    updateChildren(node, unwrap(jsx.children), subscriptions);
    if (isObservable(jsx.children)) {
        subscriptions.push(jsx.children.subscribe(function (children) {
            updateChildren(node, children, subscriptions);
        }));
    }
    if (subscriptions.length) {
        addDisposeCallback(node, function () { return subscriptions.map(function (s) { return s.dispose(); }); });
    }
    return node;
}
function getInsertTarget(possibleTemplateElement) {
    return 'content' in possibleTemplateElement
        ? possibleTemplateElement.content : possibleTemplateElement;
}
/**
 *
 * @param {HTMLElement|HTMLTemplateElement} possibleTemplateElement
 * @param {Node} toAppend
 */
function appendChildOrChildren(possibleTemplateElement, toAppend) {
    var e_1, _a;
    if (Array.isArray(toAppend)) {
        try {
            for (var toAppend_1 = __values(toAppend), toAppend_1_1 = toAppend_1.next(); !toAppend_1_1.done; toAppend_1_1 = toAppend_1.next()) {
                var node = toAppend_1_1.value;
                appendChildOrChildren(possibleTemplateElement, node);
            }
        }
        catch (e_1_1) { e_1 = { error: e_1_1 }; }
        finally {
            try {
                if (toAppend_1_1 && !toAppend_1_1.done && (_a = toAppend_1["return"])) _a.call(toAppend_1);
            }
            finally { if (e_1) throw e_1.error; }
        }
    }
    else {
        getInsertTarget(possibleTemplateElement).appendChild(toAppend);
    }
}
/**
 *
 * @param {HTMLElement|HTMLTemplateElement} possibleTemplateElement
 * @param {Node} toAppend
 * @param {Node} beforeNode
 */
function insertChildOrChildren(possibleTemplateElement, toAppend, beforeNode) {
    var e_2, _a;
    if (!beforeNode.parentNode) {
        return;
    }
    if (Array.isArray(toAppend)) {
        try {
            for (var toAppend_2 = __values(toAppend), toAppend_2_1 = toAppend_2.next(); !toAppend_2_1.done; toAppend_2_1 = toAppend_2.next()) {
                var node = toAppend_2_1.value;
                insertChildOrChildren(possibleTemplateElement, node, beforeNode);
            }
        }
        catch (e_2_1) { e_2 = { error: e_2_1 }; }
        finally {
            try {
                if (toAppend_2_1 && !toAppend_2_1.done && (_a = toAppend_2["return"])) _a.call(toAppend_2);
            }
            finally { if (e_2) throw e_2.error; }
        }
    }
    else {
        getInsertTarget(possibleTemplateElement).insertBefore(toAppend, beforeNode);
    }
}
/**
 *
 * @param {HTMLElement} node
 * @param {Array} children
 * @param {Array} subscriptions
 */
function updateChildren(node, children, subscriptions) {
    var e_3, _a;
    var lastChild = node.lastChild;
    while (lastChild) {
        removeNode(lastChild);
        lastChild = node.lastChild;
    }
    try {
        for (var _b = __values(children || []), _c = _b.next(); !_c.done; _c = _b.next()) {
            var child = _c.value;
            if (isObservable(child)) {
                subscriptions.push(monitorObservableChild(node, child));
            }
            else {
                appendChildOrChildren(node, convertJsxChildToDom(child));
            }
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b["return"])) _a.call(_b);
        }
        finally { if (e_3) throw e_3.error; }
    }
}
/**
 *
 * @param {HTMLElement} node
 * @param {string} name
 * @param {any} valueOrObservable
 */
function setNodeAttribute(node, name, valueOrObservable) {
    var value = unwrap(valueOrObservable);
    NativeProvider.addValueToNode(node, name, valueOrObservable);
    if (typeof value === 'string') {
        node.setAttribute(name, value);
    }
    else if (value === undefined) {
        node.removeAttribute(name);
    }
}
/**
 *
 * @param {HTMLElement} node
 * @param {Object} attributes
 * @param {Array} subscriptions
 */
function updateAttributes(node, attributes, subscriptions) {
    var e_4, _a;
    while (node.attributes.length) {
        node.removeAttribute(node.attributes[0].name);
    }
    var _loop_1 = function (name_1, value) {
        if (isObservable(value)) {
            subscriptions.push(value.subscribe(function (attr) { return setNodeAttribute(node, name_1, value); }));
        }
        setNodeAttribute(node, name_1, value);
    };
    try {
        for (var _b = __values(Object.entries(attributes || {})), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = __read(_c.value, 2), name_1 = _d[0], value = _d[1];
            _loop_1(name_1, value);
        }
    }
    catch (e_4_1) { e_4 = { error: e_4_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b["return"])) _a.call(_b);
        }
        finally { if (e_4) throw e_4.error; }
    }
}
/**
 *
 * @param {jsx} newJsx
 * @param {HTMLElement|Array} toReplace
 * @return {HTMLElement|Array} Nodes to replace next time
 *
 * TODO: Use trackArrayChanges to minimize changes to the DOM and state-loss.
 */
function replaceNodeOrNodes(newJsx, toReplace, parentNode) {
    var e_5, _a, e_6, _b;
    var newNodeOrNodes = convertJsxChildToDom(newJsx);
    var $context = contextFor(toReplace);
    var firstNodeToReplace = Array.isArray(toReplace)
        ? toReplace[0] || null : toReplace;
    insertChildOrChildren(parentNode, newNodeOrNodes, firstNodeToReplace);
    if (Array.isArray(toReplace)) {
        try {
            for (var toReplace_1 = __values(toReplace), toReplace_1_1 = toReplace_1.next(); !toReplace_1_1.done; toReplace_1_1 = toReplace_1.next()) {
                var node = toReplace_1_1.value;
                removeNode(node);
            }
        }
        catch (e_5_1) { e_5 = { error: e_5_1 }; }
        finally {
            try {
                if (toReplace_1_1 && !toReplace_1_1.done && (_a = toReplace_1["return"])) _a.call(toReplace_1);
            }
            finally { if (e_5) throw e_5.error; }
        }
    }
    else {
        removeNode(toReplace);
    }
    if ($context) {
        if (Array.isArray(newNodeOrNodes)) {
            try {
                for (var newNodeOrNodes_1 = __values(newNodeOrNodes), newNodeOrNodes_1_1 = newNodeOrNodes_1.next(); !newNodeOrNodes_1_1.done; newNodeOrNodes_1_1 = newNodeOrNodes_1.next()) {
                    var node = newNodeOrNodes_1_1.value;
                    applyBindings($context, node);
                }
            }
            catch (e_6_1) { e_6 = { error: e_6_1 }; }
            finally {
                try {
                    if (newNodeOrNodes_1_1 && !newNodeOrNodes_1_1.done && (_b = newNodeOrNodes_1["return"])) _b.call(newNodeOrNodes_1);
                }
                finally { if (e_6) throw e_6.error; }
            }
        }
        else {
            applyBindings($context, newNodeOrNodes);
        }
    }
    return newNodeOrNodes;
}
/**
 *
 * @param {HTMLElement} node
 * @param {jsx|Array} child
 */
function monitorObservableChild(node, child) {
    var jsx = unwrap(child);
    var toReplace = convertJsxChildToDom(jsx);
    appendChildOrChildren(node, toReplace);
    var subscription = child.subscribe(function (newJsx) {
        toReplace = replaceNodeOrNodes(newJsx, toReplace, node);
    });
    return subscription;
}
/**
 * Convert a child to the anticipated HTMLElement(s).
 * @param {string|array|jsx} child
 * @return {Array|Comment|HTMLElement}
 */
function convertJsxChildToDom(child) {
    return Array.isArray(child)
        ? child.map(convertJsxChildToDom)
        : child ? jsxToNode(child)
            : document.createComment('[jsx placeholder]');
}

export { maybeJsx, cloneNodeFromOriginal, jsxToNode };
//# sourceMappingURL=tko.utils.jsx.js.map
